import React, { useEffect, useRef, useState } from 'react';
import GoogleAdsIcon from './icons/GoogleAdsIcon';
import MetaAdsIcon from './icons/MetaAdsIcon';
import BingAdsIcon from './icons/BingAdsIcon';
import TikTokAdsIcon from './icons/TikTokAdsIcon';
import ChaosAnimation from './ChaosAnimation';
import { t } from '../translations';

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isAnimationVisible, setIsAnimationVisible] = useState(true);
  const [shouldRenderChaos, setShouldRenderChaos] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(([entry]) => setIsAnimationVisible(entry.isIntersecting), { rootMargin: '200px' });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShouldRenderChaos(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleCTAClick = (text: string, location: string) => {
    window.dataLayer?.push({
        event: 'cta_click',
        cta_text: text,
        cta_location: location
    });
  };

  const handleLinkClick = (text: string, url: string, location: string) => {
    window.dataLayer?.push({
        event: 'navigation_click',
        link_text: text,
        link_url: url,
        link_location: location
    });
  };

  return (
    <section ref={sectionRef} id="home" className="relative pt-24 pb-12 md:pt-32 md:pb-16 overflow-hidden">
      {shouldRenderChaos && isAnimationVisible && (
        <div className="absolute top-0 left-0 right-0 h-[65%] lg:hidden z-0 overflow-hidden opacity-50 pointer-events-none">
            <ChaosAnimation count={60} />
        </div>
      )}
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="text-center lg:text-left">
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-gray-900 mb-6 max-w-2xl mx-auto lg:mx-0 leading-tight animate-fade-in-up">
              {t.hero.titleStart} <span className="bg-primary-600 text-white px-4 rounded-lg inline-block transform -skew-x-3">{t.hero.titleHighlight}</span>
            </h1>
            <p className="max-w-xl mx-auto lg:mx-0 text-lg text-gray-600 mb-10 animate-fade-in-up">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start items-center gap-4">
              <a 
                href="#contact" 
                onClick={() => { handleCTAClick(t.hero.ctaPrimary, 'hero_primary'); handleLinkClick(t.hero.ctaPrimary, '#contact', 'hero_section'); }}
                className="bg-primary-600 text-white font-bold py-3 px-8 rounded-md text-lg hover:bg-primary-700 transition-all w-full sm:w-auto shadow-lg"
              >
                {t.hero.ctaPrimary}
              </a>
              <a 
                href="#case-studies" 
                onClick={() => { handleCTAClick(t.hero.ctaSecondary, 'hero_secondary'); handleLinkClick(t.hero.ctaSecondary, '#case-studies', 'hero_section'); }}
                className="bg-white/80 backdrop-blur-sm text-gray-800 font-bold py-3 px-8 rounded-md text-lg hover:bg-gray-100 transition-all border border-gray-300 w-full sm:w-auto"
              >
                {t.hero.ctaSecondary}
              </a>
            </div>
          </div>
          <div className="hidden lg:flex justify-center items-center h-full min-h-[500px]">
            {shouldRenderChaos && isAnimationVisible ? <ChaosAnimation count={200} /> : <div className="w-full h-full" />}
          </div>
        </div>
        <div className="mt-20">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-6 text-center">{t.hero.experts}</p>
            <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400">
                <div className="flex items-center gap-3 text-gray-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-100 shadow-sm"><GoogleAdsIcon /> <span className="font-semibold text-lg">Google Ads</span></div>
                <div className="flex items-center gap-3 text-gray-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-100 shadow-sm"><MetaAdsIcon /> <span className="font-semibold text-lg">Meta Ads</span></div>
                <div className="flex items-center gap-3 text-gray-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-100 shadow-sm"><BingAdsIcon /> <span className="font-semibold text-lg">Bing Ads</span></div>
                <div className="flex items-center gap-3 text-gray-500 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-100 shadow-sm"><TikTokAdsIcon /> <span className="font-semibold text-lg">TikTok Ads</span></div>
            </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;